// src/lib/invoiceDiscrepancy.js
// #301, Session 74z4. The discrepancy resolution vocabulary, the first-pass rule,
// and the mutation behind them.
//
// One caller today — InvoicingPage.js — so this is NOT an extraction under
// standing rule 14, which is about a mutation/side-effect sequence duplicated
// across callers. It exists for the reason shortClose.js gives for its own
// constants: the resolution codes and the event types are spelled in a database
// CHECK constraint, and importing them rather than typing them is what stops a
// call site drifting silently out of agreement with the constraint.
//
// The first-pass rule lives here rather than inline on the page because two
// things read it and they must not disagree about what the number means.

import { supabase } from './supabase';

// The two resolution codes, spelled the way the CHECK constraint spells them.
export const PRICE_ACCEPTED = 'price_accepted';
export const FLAGGED_IN_ERROR = 'flagged_in_error';

// The two event types, spelled the way the notifications CHECK constraint spells
// them. Added in 20260822_invoice_discrepancy_resolution.sql.
export const EVENT_AWAITING_ENTRY = 'invoice_awaiting_entry';
export const EVENT_DISCREPANCY_RESOLVED = 'invoice_discrepancy_resolved';

// Invoicing is its own notification family per #284's decision 1, so this is a
// new domain rather than a copy of purchasing's. sendNotification() takes it as
// its FIFTH POSITIONAL argument, not a named property.
export const INVOICING_DOMAIN = 'invoicing';

// THE DISTINCTION THIS FILE EXISTS FOR. Two different things end a discrepancy
// and only one of them says the invoice was never a mismatch.
//
// price_accepted   - the difference was real and D&R agreed to pay it. That
//                    invoice genuinely was not a first-pass match and must keep
//                    counting against the rate forever.
// flagged_in_error - there was no mismatch (tax, rounding, a threshold set too
//                    tight). The flag should never have fired, so the invoice
//                    leaves the rate entirely rather than damaging it forever.
export const OUTCOMES = [
  {
    code: PRICE_ACCEPTED,
    label: 'Price accepted',
    effect: 'Stays counted against first-pass match rate.',
    prompt: 'Why was the price accepted?',
    confirm: 'Accept price and resolve',
  },
  {
    code: FLAGGED_IN_ERROR,
    label: 'Flagged in error',
    effect: 'Removed from first-pass match rate.',
    prompt: 'Why was this not a real discrepancy?',
    confirm: 'Clear false flag',
  },
];

export function outcomeFor(code) {
  return OUTCOMES.find(o => o.code === code) || null;
}

// Quick-pick reasons. The fast path, not the record: picking a chip fills the
// free-text field, so the two-tap mobile case works and anyone with more to say
// can still type it. Deliberately NOT a constrained vocabulary in the database,
// for the reason #288 gives — the reason has to survive a case nobody listed.
export const PRICE_ACCEPTED_REASONS = [
  'Freight added after order',
  'Price increase agreed',
  'Substitute item, higher cost',
  'Under tolerance, not worth disputing',
];

export const FLAGGED_IN_ERROR_REASONS = [
  'Tax included on the invoice',
  'Rounding only',
  'Threshold set too tight',
  'Order price was entered wrong',
];

export function reasonsFor(code) {
  return code === FLAGGED_IN_ERROR ? FLAGGED_IN_ERROR_REASONS : PRICE_ACCEPTED_REASONS;
}

// ── State reads ──────────────────────────────────────────────────────────────

// An invoice is an OPEN discrepancy while it is flagged and not yet resolved.
// This is what the Discrepancies tab counts, and it is the only predicate that
// changed shape at #301 — before it, "flagged" and "open" were the same thing.
export function isOpenDiscrepancy(inv) {
  return !!inv?.discrepancy_flagged && !inv?.discrepancy_resolved;
}

export function isResolved(inv) {
  return !!inv?.discrepancy_flagged && !!inv?.discrepancy_resolved;
}

// The who/when/why/which behind a resolution, for the reversal panel. Returns
// null when the invoice is not resolved, and also when it is but the columns did
// not come back from the query — worth failing quietly rather than rendering
// "resolved by undefined".
export function resolutionDetail(inv) {
  if (!isResolved(inv)) return null;
  return {
    at: inv.discrepancy_resolved_at,
    code: inv.discrepancy_resolution,
    outcome: outcomeFor(inv.discrepancy_resolution),
    reason: inv.discrepancy_resolution_reason,
    byName: inv.discrepancy_resolved_by_user?.full_name || null,
  };
}

// ── The first-pass rule, in one place ────────────────────────────────────────

// An invoice resolved as flagged-in-error was never a mismatch, so it leaves the
// calculation entirely rather than sitting in the denominator as a permanent
// penalty for a threshold that was set wrong.
export function isFalseFlag(inv) {
  return isResolved(inv) && inv.discrepancy_resolution === FLAGGED_IN_ERROR;
}

// THE FLAG IS NOT CLEARED BY RESOLVING, and this function is why that matters.
// firstPassRate is the share of invoices that reconciled with no manual
// intervention. An invoice that was flagged and then resolved DID require
// intervention, so it must stay in the denominator and out of the numerator.
// Were resolving to clear discrepancy_flagged instead, this number would RISE
// every time somebody did the work of resolving something — the opposite of what
// it measures.
export function firstPassRate(invoices) {
  const counted = (invoices || []).filter(inv => !isFalseFlag(inv));
  if (counted.length === 0) return null;
  const matched = counted.filter(inv => !inv.discrepancy_flagged).length;
  return Math.round((matched / counted.length) * 100);
}

// ── The mutation ─────────────────────────────────────────────────────────────

// Returns the function's jsonb result, which reports how many open discrepancies
// the order has left. The caller uses that to decide what to SAY, never to decide
// what to write.
//
// Reversal passes resolved: false with no outcome and no reason — an undo is not
// a decision, the same call #288 made for its reopen.
export async function setInvoiceResolution({ invoiceId, userId, resolved, outcome, reason }) {
  const { data, error } = await supabase.rpc('set_material_request_invoice_resolution', {
    p_invoice_id: invoiceId,
    p_user_id: userId,
    p_resolved: resolved,
    p_resolution: resolved ? outcome : null,
    p_reason: resolved && reason != null ? reason : null,
  });
  if (error) throw error;
  return data;
}
